import { zodResolver } from '@hookform/resolvers/zod';
import { Add, Delete } from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  getHolds,
  getLpSuppliers,
  getMarkets,
  getPorts,
} from '../../../master-data';
import IDOptionLabel, {
  getIDOptionLabel,
} from '../../../../shared/components/mbui/IDOptionLabel';
import RHFAutocomplete from '../../../../shared/components/react-hook-form/RHFAutocomplete';
import RHFDatePicker from '../../../../shared/components/react-hook-form/RHFDatePicker';
import RHFForm from '../../../../shared/components/react-hook-form/RHFForm';
import RHFNumber from '../../../../shared/components/react-hook-form/RHFNumber';

const holdSchema = z.object({
  hold: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: 'Hold is required',
    }),
  volume: z
    .preprocess((v) => (typeof v === 'string' ? Number(v || 0) : v), z.number())
    .refine((v) => v >= 0, { message: 'Volume must be >= 0' }),
});

const formSchema = z.object({
  market: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: 'Market is required',
    }),
  portOperator: z
    .string()
    .nullable()
    .refine((v) => v !== null && v.length > 0, {
      message: 'Port Operator is required',
    }),
  actualInflow: z
    .preprocess((v) => (typeof v === 'string' ? Number(v || 0) : v), z.number())
    .refine((v) => v >= 0, { message: 'Actual Inflow must be >= 0' }),
  forecastInflow: z
    .preprocess((v) => (typeof v === 'string' ? Number(v || 0) : v), z.number())
    .refine((v) => v >= 0, { message: 'Forecast Inflow must be >= 0' }),
  currentStock: z
    .preprocess((v) => (typeof v === 'string' ? Number(v || 0) : v), z.number())
    .refine((v) => v >= 0, { message: 'Current Stock must be >= 0' }),
  departurePort: z.string().min(1, { message: 'Departure Port is required' }),
  destinationPort: z
    .string()
    .min(1, { message: 'Destination Port is required' }),
  date: z
    .any()
    .refine((v) => !!v, { message: 'Date is required' })
    .refine(
      (v) =>
        !v ||
        dayjs(v).isSame(dayjs(), 'day') ||
        dayjs(v).isAfter(dayjs(), 'day'),
      {
        message: 'Date cannot be in the past',
      }
    ),
  holds: z.array(holdSchema),
});

export type RHFStockFormValues = z.infer<typeof formSchema>;

const RHFStockForm = forwardRef<
  HTMLFormElement,
  { onSubmit?: (data: RHFStockFormValues) => void }
>(({ onSubmit: propsOnSubmit = () => undefined }, ref) => {
  const [marketOptions, setMarketOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [portOperatorOptions, setPortOperatorOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [portOptions, setPortOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [holdOptions, setHoldOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const defaultValues: RHFStockFormValues = useMemo(
    () => ({
      market: null,
      portOperator: null,
      actualInflow: 0,
      forecastInflow: 0,
      currentStock: 0,
      departurePort: '',
      destinationPort: '',
      date: dayjs(),
      holds: [{ hold: null, volume: 0 }],
    }),
    []
  );

  const methods = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  });

  const { control } = methods;
  const { fields, append, remove } = useFieldArray({ name: 'holds', control });

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const markets = await getMarkets();
        const options = markets
          .map((v: any) => ({ label: v.name, value: String(v.id) }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setMarketOptions(options);
      } catch (error) {
        console.error('Error fetching markets:', error);
      }
    };
    fetchMarkets();
  }, []);

  useEffect(() => {
    const fetchPortOperators = async () => {
      try {
        const portOperatorData = await getLpSuppliers({ isPortOperator: true });
        const options = portOperatorData
          .map((v: any) => ({
            label: v.supplierName,
            value: String(v.supplierNo),
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setPortOperatorOptions(options);
      } catch (error) {
        console.error('Error fetching port operators:', error);
      }
    };
    fetchPortOperators();
  }, []);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const ports = await getPorts();
        const options = ports
          .map((port: any) => ({
            label: `${port.unLocode} ${port.name}`,
            value: port.unLocode,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setPortOptions(options);
      } catch (error) {
        console.error('Error fetching ports:', error);
      }
    };
    fetchPorts();
  }, []);

  useEffect(() => {
    const fetchHoldOptions = async () => {
      try {
        const holds = await getHolds();
        const options = holds
          .map((hold: any) => ({ label: hold.name, value: String(hold.id) }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setHoldOptions(options);
      } catch (error) {
        console.error('Error fetching hold options:', error);
      }
    };
    fetchHoldOptions();
  }, []);

  const toNumberIfNumeric = (v: string | null) => {
    if (v === null) return null;
    const n = Number(v);
    return isNaN(n) ? v : (n as unknown as any);
  };

  const onSubmit = (data: RHFStockFormValues) => {
    const normalized = {
      ...data,
      date: dayjs(data.date).startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      market: toNumberIfNumeric(data.market as any),
      portOperator: toNumberIfNumeric(data.portOperator as any),
      holds: data.holds.map((h) => ({
        ...h,
        hold: toNumberIfNumeric(h.hold as any),
      })),
    } as RHFStockFormValues as any;

    propsOnSubmit(normalized);
  };

  return (
    <RHFForm {...methods} onSubmit={onSubmit} ref={ref}>
      <Stack direction='column' spacing={2}>
        {/* Market Details */}
        <Stack direction='column' spacing={2}>
          <Typography variant='h5'>Market Details</Typography>
          <FormGroup>
            <Stack direction='row' gap={2}>
              <RHFAutocomplete
                name='market'
                options={marketOptions}
                isOptionEqualToValue={(option: any, value: any) =>
                  option?.value === value?.value
                }
                getOptionLabel={getIDOptionLabel}
                renderOption={(props: any, option: any) => (
                  <li {...props} key={option.value}>
                    <IDOptionLabel id={option.value} label={option.label} />
                  </li>
                )}
                placeholder='Select Market'
                label='Market'
                required
              />
              <RHFAutocomplete
                name='portOperator'
                options={portOperatorOptions}
                label='Port Operator'
                required
              />
            </Stack>
          </FormGroup>
        </Stack>

        {/* Stock Details */}
        <FormGroup>
          <Stack direction='column' gap={2}>
            <Typography variant='h5'>Stock Details</Typography>
            <Stack direction='row' gap={2}>
              <FormControl fullWidth>
                <RHFNumber name='actualInflow' label='Actual Inflow' />
              </FormControl>

              <FormControl fullWidth>
                <RHFDatePicker name='date' label='Date' disablePast />
              </FormControl>
            </Stack>
            <Stack direction='row' gap={2}>
              <FormControl fullWidth>
                <RHFNumber name='currentStock' label='Current Stock' />
              </FormControl>
              <FormControl fullWidth>
                <RHFNumber name='forecastInflow' label='Forecast Inflow' />
              </FormControl>
            </Stack>
          </Stack>
        </FormGroup>

        {/* Port Details */}
        <FormGroup>
          <Stack direction='column' gap={2}>
            <Typography variant='h5'>Port Details</Typography>
            <Stack direction='row' gap={2}>
              <RHFAutocomplete
                name='departurePort'
                options={portOptions}
                label='Departure Port'
                required
              />
              <RHFAutocomplete
                name='destinationPort'
                options={portOptions}
                label='Destination Port'
                required
              />
            </Stack>
          </Stack>
        </FormGroup>

        {/* Holdings */}
        <FormGroup>
          <Typography variant='h5'>Holdings ({fields.length})</Typography>
          {fields.length === 0 && (
            <Typography variant='body2' color='textSecondary'>
              No holds added
            </Typography>
          )}

          {fields.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 1, boxShadow: 'none' }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography fontSize={'14px'}></Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: '250px' }}>
                      <Typography fontSize={'14px'}>Hold</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={'14px'}>Volume</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={'14px'}></Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={field.id}>
                      <TableCell>
                        <Typography variant='body2' color='textSecondary'>
                          {index + 1}.
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <RHFAutocomplete
                          name={`holds.${index}.hold`}
                          options={holdOptions}
                          label='Hold'
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <RHFNumber
                          name={`holds.${index}.volume`}
                          variant='standard'
                          InputProps={{ disableUnderline: true }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <IconButton
                          color='error'
                          size='small'
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                        >
                          <Delete fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Button
            type='button'
            onClick={() => append({ hold: null, volume: 0 })}
            variant='text'
            startIcon={<Add />}
            color='primary'
            sx={{ mt: 2 }}
          >
            Add Hold
          </Button>
        </FormGroup>

        <Button type='submit' style={{ display: 'none' }} aria-hidden='true' />
      </Stack>
    </RHFForm>
  );
});

export default RHFStockForm;
